import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, Sparkles, CheckCircle2, ShieldCheck, Crown } from "lucide-react";

export default function PremiumPaymentModal({ open, onOpenChange }) {
  const { user } = useAuth();

  const handleWhatsAppActivation = () => {
    const adminWa = process.env.REACT_APP_ADMIN_WA || "6281703200075";
    const name = user?.name || "-";
    const phone = user?.phone || "-";
    const email = user?.email || "-";

    const message = `Halo Weddly Admin, saya ingin meng-upgrade akun saya ke Paket Premium 1 Tahun (Rp 45.000).\n\nDetail Akun:\n• Nama: ${name}\n• No. HP: ${phone}\n• Email: ${email}\n\nMohon petunjuk aktivasi via WhatsApp. Terima kasih!`;

    window.open(`https://wa.me/${adminWa}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#FFFCF9] border-[#ECE7DF] max-h-[90vh] flex flex-col p-6">
        <DialogHeader className="pb-3 border-b border-[#F1E5E7]">
          <div className="flex items-center gap-2">
            <Badge className="bg-[#BE5366] text-white text-[10px] uppercase font-bold tracking-wider">
              Special Launching
            </Badge>
            <span className="text-xs text-[#738A78] font-bold flex items-center gap-1">
              <Sparkles className="h-3.5 w-3.5" /> Hemat 59%
            </span>
          </div>
          <DialogTitle className="font-serif-title text-2xl text-[#35282C] mt-1 flex items-center gap-2">
            <Crown className="h-6 w-6 text-amber-500 shrink-0" />
            Upgrade Weddly Premium
          </DialogTitle>
          <p className="text-xs text-[#90777D] mt-0.5">
            Akses tanpa batas seluruh modul perencanaan pernikahan untuk Anda &amp; pasangan.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-4 py-3">
          {/* Price Overview Banner */}
          <div className="p-4 rounded-2xl bg-[#F9EFF0] border border-[#F1E5E7] flex items-center justify-between">
            <div>
              <span className="text-xs text-[#90777D] line-through font-semibold">Rp 110.000</span>
              <div className="flex items-baseline gap-1.5">
                <span className="font-serif-title text-3xl font-bold text-[#BE5366]">Rp 45.000</span>
                <span className="text-xs text-[#90777D]">/ 365 Hari</span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-[11px] font-semibold text-[#738A78] block">✓ Sekali Bayar</span>
              <span className="text-[10px] text-[#90777D]">Tanpa Perpanjangan Otomatis</span>
            </div>
          </div>

          {/* Premium Features List */}
          <div className="space-y-2 bg-white p-4 rounded-xl border border-[#F1E5E7]">
            <p className="text-xs font-bold text-[#35282C] mb-2 uppercase tracking-wider">
              Fitur yang Anda Dapatkan:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-[#35282C]">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#BE5366] shrink-0" />
                <span>Akses Penuh Seluruh Modul</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#BE5366] shrink-0" />
                <span>Undangan Digital &amp; Musik</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#BE5366] shrink-0" />
                <span>Wedding Moodboard HD</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#BE5366] shrink-0" />
                <span>Weddly AI Assistant</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#BE5366] shrink-0" />
                <span>Keuangan, Tabungan &amp; Tamu</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#BE5366] shrink-0" />
                <span>Honeymoon Planner</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#BE5366] shrink-0" />
                <span>Dokumen &amp; Legalitas</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#BE5366] shrink-0" />
                <span>Seserahan &amp; Mahar</span>
              </div>
            </div>
          </div>

          {/* User Account Info Summary Box */}
          {user && (
            <div className="p-3.5 rounded-xl bg-muted/40 border border-border/60 space-y-1 text-xs">
              <p className="font-semibold text-muted-foreground">Akun Yang Akan Di-upgrade:</p>
              <div className="flex justify-between items-center pt-0.5">
                <span className="font-bold text-foreground">{user.name || "User"}</span>
                <span className="font-mono text-muted-foreground">{user.phone}</span>
              </div>
            </div>
          )}

          {/* WhatsApp Activation Button */}
          <div className="pt-2 space-y-2">
            <Button
              type="button"
              onClick={handleWhatsAppActivation}
              className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-sm h-12 rounded-full shadow-md transition-all flex items-center justify-center gap-2"
              data-testid="whatsapp-activation-btn"
            >
              <MessageCircle className="h-5 w-5" />
              <span>Aktivasi via WhatsApp</span>
            </Button>
            <p className="text-[11px] text-center text-[#90777D]">
              Klik tombol di atas untuk menghubungi Admin Weddly via WhatsApp dan menyelesaikan instruksi aktivasi.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
